import React, { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import CustomerModel from '../../../components/Customers/CustomerModel';
import { isValidMobilePhoneNumber } from '../../../services/phoneService';
import { WS_CALL_SIDEBAR_ACTIONS } from '../../../constants/actionsConstants';
import useCallPermissionActions from '../../hooks/fonema/actions/useCallPermissionActions';
import useFonemaCallBusy from '../../hooks/fonema/useFonemaCallBusy';
import { useWhatsappCallsConnected, useWhatsappCallsUiEnabled } from '../../hooks/calls/useCallProvider';
import { sharingInformationService } from '../../../services/sharingInformationService';
import { canUserMakeCalls } from '../../utils/callsPermissionsUtils';
import CustomerRowMC from './CustomerRowMC';
import {
  SearchDebounceMs,
  ClientsSearchLimit,
  getClientPhone,
  appendCallingPermissionToClient
} from './customerListUtils';

/**
 * CustomerListMC Component
 *
 * Displays a searchable list of WhatsApp customers assigned to the current user.
 * Includes a search bar with debounce functionality and shows customer information
 * with country flags. Each customer has an action button to initiate a call,
 * which first checks for call permissions.
 *
 * Features:
 * - Real-time search with 500ms debounce
 * - Country flag icons based on phone numbers
 * - Loading states and empty states
 * - Integration with Redux for state management
 * - Automatic permission check before calling
 *
 * @param {Object} props - Component props
 * @param {Function} props.onSelectClient - Callback function when a client is selected
 */
const CustomerListMC = ({ onSelectClient, onClose }) => {
  const { t } = useTranslation('Inbox');
  const dispatch = useDispatch();
  const { clients, loading, searchText, fetchError } = useSelector((state) => state.wsCallSidebarReducer);
  const currentRetailerUser = useSelector((state) => state.mainReducer?.currentRetailerUser);
  const callsFeatureEnabled = useWhatsappCallsConnected();
  const callsRuntimeReady = useWhatsappCallsUiEnabled();
  const canMakeCalls = canUserMakeCalls(currentRetailerUser);

  const {
    checkPermission,
    checkingPermission,
    mustConfirmFrom,
    outboundLinesLoading,
    isVoiceProvider
  } = useCallPermissionActions();
  const fonemaCallBusy = useFonemaCallBusy();

  const fetchClients = useCallback(async (text = '') => {
    try {
      dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.SET_FETCH_ERROR, payload: false });
      const response = await CustomerModel.searchAssignedCustomers({
        text,
        serialized: true,
        limit: ClientsSearchLimit
      });

      const customers = response?.customers || [];
      const clientsWithValidMobile = customers.filter((customer) => isValidMobilePhoneNumber(
        getClientPhone(customer),
        customer.country_id
      ));
      dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.SET_CLIENTS, payload: clientsWithValidMobile });
    } catch {
      dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.SET_FETCH_ERROR, payload: true });
      dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.SET_CLIENTS, payload: [] });
    } finally {
      dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.SET_LOADING, payload: false });
    }
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClients(searchText);
    }, SearchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchText, fetchClients]);

  const handleSearch = useCallback((e) => {
    dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.SET_SEARCH_TEXT, payload: e.target.value });
  }, [dispatch]);

  const startCallWithClient = useCallback((client) => {
    sharingInformationService.setSubject({
      InitiateCall: true,
      customer: client
    });
    sharingInformationService.setSubject({});
    onClose?.();
  }, [onClose]);

  const handleClientClick = useCallback(async (client) => {
    if (!canMakeCalls || fonemaCallBusy || !callsRuntimeReady) return;
    // Voice multi-line: wait for SIP-ready lines; Fonema never loads outbound lines.
    if (isVoiceProvider && outboundLinesLoading) return;

    // Voice + more than one SIP-ready line: open dialer; permission after selector.
    if (isVoiceProvider && mustConfirmFrom) {
      startCallWithClient(client);
      return;
    }

    const result = await checkPermission(client);
    if (!result) return;

    if (result?.canStartCall) {
      startCallWithClient(client);
    } else {
      onSelectClient(appendCallingPermissionToClient(client, result), 'REQUEST_PERMISSION');
    }
  }, [
    canMakeCalls,
    fonemaCallBusy,
    callsRuntimeReady,
    isVoiceProvider,
    outboundLinesLoading,
    mustConfirmFrom,
    checkPermission,
    startCallWithClient,
    onSelectClient
  ]);

  const emptyStateMessage = useMemo(() => {
    if (loading || clients.length > 0) return null;
    if (fetchError) return t('wsCallSidebar.clientsFetchError');
    return t('wsCallSidebar.noClientsFound');
  }, [loading, clients.length, fetchError, t]);

  if (!callsFeatureEnabled) {
    return <>{t('wsCallSidebar.callsFeatureNotEnabled')}</>;
  }

  if (!canMakeCalls) {
    return <>{t('wsCallSidebar.makeCallsPermissionDenied', { defaultValue: 'No tienes permiso para realizar llamadas.' })}</>;
  }

  if (!callsRuntimeReady) {
    return <>{t('wsCallSidebar.callsRuntimeNotConfigured')}</>;
  }

  return (
    <>
      <div className="tw-pb-2">
        <div className="tw-relative">
          <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="tw-h-5 tw-w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </span>
          <input
            type="text"
            value={searchText}
            onChange={handleSearch}
            placeholder={t('wsCallSidebar.searchPlaceholder')}
            className="tw-w-full tw-py-2 tw-pl-10 tw-pr-4 tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
          />
        </div>
      </div>

      <div className="tw-flex-1 tw-overflow-y-auto">
        {loading && clients.length === 0 ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-32">
            <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-500" />
          </div>
        ) : (
          clients.map((client) => (
            <CustomerRowMC
              key={client.id}
              client={client}
              isChecking={checkingPermission === client.id}
              isBusy={fonemaCallBusy}
              onCall={handleClientClick}
              callLabel={t('wsCallSidebar.call')}
            />
          ))
        )}
        {emptyStateMessage && (
          <div className="tw-text-center tw-py-8 tw-text-gray-500">
            {emptyStateMessage}
          </div>
        )}
      </div>
    </>
  );
};

export default CustomerListMC;
